// src/features/calls/providers/googleMeetAdapter.ts
// Google Meet provider adapter for ChatHub call orchestration.
//
// Creates a Google Calendar event with conferenceData to get a real
// Google Meet link. Falls back to URL-based (meet.new / workspace default)
// when the provider-backed path is unavailable or fails.

import type { ProviderAdapterResult } from '../callOrchestrationService';
import type { AsyncResult, CalendarConnectionError } from '../../calendar/integrations/types';
import { GoogleCalendarAuthService } from '../../calendar/integrations/GoogleCalendarAuthService';
import { GoogleCalendarSyncService } from '../../calendar/integrations/GoogleCalendarSyncService';
import { resolveGoogleMeetJoinUrl } from './googleMeetProvider';
import { isDemoMode } from '../../../lib/supabase';
import { CALENDAR_SERVER_INTEGRATION_AVAILABLE } from '../../calendar/integrations/calendarSecurityPolicy';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const GOOGLE_CALENDAR_API = 'https://www.googleapis.com/calendar/v3';
const LOG_TAG = '[googleMeetAdapter]';

// ─────────────────────────────────────────────────────────────────────────────
// Google Calendar API Types (conferenceData subset)
// ─────────────────────────────────────────────────────────────────────────────

/** Conference entry point from Google Calendar API response. */
interface GoogleConferenceEntryPoint {
    entryPointType: 'video' | 'phone' | 'sip' | 'more';
    uri: string;
    label?: string;
}

/** Conference data from a Google Calendar event response. */
interface GoogleConferenceData {
    conferenceId?: string;
    conferenceSolution?: {
        key?: { type: string };
        name?: string;
        iconUri?: string;
    };
    entryPoints?: GoogleConferenceEntryPoint[];
    createRequest?: {
        requestId: string;
        conferenceSolutionKey: { type: string };
        status: { statusCode: string };
    };
}

/** Minimal Google Calendar event response shape we care about. */
interface GoogleEventResponse {
    id: string;
    htmlLink?: string;
    conferenceData?: GoogleConferenceData;
    status?: string;
    updated?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Adapter Configuration
// ─────────────────────────────────────────────────────────────────────────────

export interface GoogleMeetAdapterConfig {
    /** Workspace-configured default meet URL (may be null) */
    defaultMeetUrl?: string | null;
    /** Room the call belongs to (used for event naming) */
    roomId: string;
    /** Call intent — audio or video */
    intent: 'call' | 'video';
    /** Optional agenda for planned calls */
    agenda?: string;
    /** Optional scheduled start time (ISO 8601) for planned calls */
    scheduledStartAt?: string | null;
    /** Optional duration in minutes for planned calls */
    scheduledDurationMinutes?: number | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Extract the video entry point URI from Google conferenceData.
 */
function extractMeetJoinUrl(conferenceData?: GoogleConferenceData): string | null {
    if (!conferenceData?.entryPoints) return null;
    const videoEntry = conferenceData.entryPoints.find(
        (ep) => ep.entryPointType === 'video' && ep.uri,
    );
    return videoEntry?.uri ?? null;
}

/**
 * Build a URL-based fallback result (meet.new or workspace default).
 */
function buildFallbackResult(defaultMeetUrl?: string | null): ProviderAdapterResult {
    const joinUrl = resolveGoogleMeetJoinUrl({ defaultMeetUrl });
    return {
        joinUrl,
        externalMeetingId: null,
        calendarEventId: null,
        providerStatus: 'fallback',
        providerMetadata: null,
    };
}

/**
 * Generate a unique request ID for conferenceData creation.
 * Google requires a unique requestId per conference creation request.
 */
function generateConferenceRequestId(): string {
    return `ax1-meet-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Build start/end timestamps for the Google Calendar event.
 * For instant calls: now → now + 1 hour.
 * For planned calls: scheduled start → scheduled start + duration.
 */
function buildEventTimes(
    scheduledStartAt?: string | null,
    scheduledDurationMinutes?: number | null,
): { start: string; end: string } {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const startDate = scheduledStartAt ? new Date(scheduledStartAt) : new Date();
    const durationMs = (scheduledDurationMinutes ?? 60) * 60 * 1000;
    const endDate = new Date(startDate.getTime() + durationMs);

    // Google Calendar API accepts ISO 8601 with timezone offset;
    // we rely on the timeZone field alongside dateTime.
    void timeZone; // referenced below in the event body
    return {
        start: startDate.toISOString(),
        end: endDate.toISOString(),
    };
}

// ─────────────────────────────────────────────────────────────────────────────
// Core: Create Google Calendar Event with Meet Conference
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Creates a Google Calendar event with Meet conferenceData attached.
 * Returns the full API response or an error.
 */
async function createCalendarEventWithMeet(
    accessToken: string,
    calendarId: string,
    config: GoogleMeetAdapterConfig,
): Promise<AsyncResult<GoogleEventResponse>> {
    const { roomId, intent, agenda, scheduledStartAt, scheduledDurationMinutes } = config;
    const times = buildEventTimes(scheduledStartAt, scheduledDurationMinutes);
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const intentLabel = intent === 'video' ? 'Video Call' : 'Call';
    const summary = agenda
        ? `📞 AX1 ${intentLabel}: ${agenda.slice(0, 80)}`
        : `📞 AX1 ${intentLabel} (Room ${roomId})`;

    const eventBody = {
        summary,
        description: [
            agenda ?? '',
            '',
            '─────────────────────────────',
            '📌 Created via AX1 | Capital ChatHub',
            `Room: ${roomId}`,
            `Intent: ${intent}`,
        ].join('\n'),
        start: { dateTime: times.start, timeZone },
        end: { dateTime: times.end, timeZone },
        conferenceData: {
            createRequest: {
                requestId: generateConferenceRequestId(),
                conferenceSolutionKey: { type: 'hangoutsMeet' },
            },
        },
        extendedProperties: {
            private: {
                ax1Source: 'chathub_call',
                ax1RoomId: roomId,
                ax1Intent: intent,
                ax1CreatedAt: new Date().toISOString(),
            },
        },
    };

    try {
        const url = `${GOOGLE_CALENDAR_API}/calendars/${encodeURIComponent(calendarId)}/events?conferenceDataVersion=1`;

        const response = await fetch(url, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(eventBody),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const msg = (errorData as { error?: { message?: string } }).error?.message
                ?? `HTTP ${response.status}`;
            return {
                success: false,
                error: {
                    code: response.status === 429 ? 'RATE_LIMITED' : 'API_ERROR',
                    message: `Google Calendar API error: ${msg}`,
                    details: JSON.stringify(errorData),
                    recoverable: true,
                    occurredAt: new Date().toISOString(),
                } satisfies CalendarConnectionError,
            };
        }

        const created: GoogleEventResponse = await response.json();
        return { success: true, data: created };
    } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        return {
            success: false,
            error: {
                code: 'NETWORK_ERROR',
                message: `Failed to create Google Calendar event: ${message}`,
                recoverable: true,
                occurredAt: new Date().toISOString(),
            } satisfies CalendarConnectionError,
        };
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Public Adapter
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Google Meet provider adapter.
 *
 * Attempts to create a Google Calendar event with conferenceData to obtain
 * a provider-backed Meet link. Falls back to URL-based resolution
 * (workspace default / meet.new) when:
 *   - Google Calendar is not connected
 *   - Token retrieval fails
 *   - Calendar ID resolution fails
 *   - Event creation API call fails
 *   - Response does not contain a video entry point
 *
 * The fallback path preserves existing behavior exactly.
 */
export async function googleMeetAdapter(
    config: GoogleMeetAdapterConfig,
): Promise<ProviderAdapterResult> {
    // Demo never claims an external provider event was created. A verified
    // participant URL can still flow through the URL-only fallback adapter.
    if (isDemoMode) {
        return {
            ...buildFallbackResult(config.defaultMeetUrl),
            providerMetadata: { demo: true, provider: 'google_meet', externalEventCreated: false },
        };
    }

    if (!CALENDAR_SERVER_INTEGRATION_AVAILABLE) {
        return buildFallbackResult(config.defaultMeetUrl);
    }

    // ── Check connection ─────────────────────────────────────────────────
    const authService = GoogleCalendarAuthService.getInstance();
    if (!authService.isConnected()) {
        console.info(LOG_TAG, 'Google Calendar not connected — using fallback');
        return buildFallbackResult(config.defaultMeetUrl);
    }

    // ── Get access token ─────────────────────────────────────────────────
    const tokenResult = await authService.getValidAccessToken();
    if (!tokenResult.success) {
        console.warn(LOG_TAG, 'Token retrieval failed — using fallback', tokenResult.error.message);
        return buildFallbackResult(config.defaultMeetUrl);
    }
    const accessToken = tokenResult.data;

    // ── Resolve AX1 calendar ID ──────────────────────────────────────────
    const syncService = GoogleCalendarSyncService.getInstance();
    const calendarResult = await syncService.getAx1CalendarId();
    if (!calendarResult.success) {
        console.warn(LOG_TAG, 'Calendar ID resolution failed — using fallback', calendarResult.error.message);
        return buildFallbackResult(config.defaultMeetUrl);
    }
    const calendarId = calendarResult.data;

    // ── Create calendar event with conferenceData ────────────────────────
    const eventResult = await createCalendarEventWithMeet(accessToken, calendarId, config);
    if (!eventResult.success) {
        console.warn(LOG_TAG, 'Event creation failed — using fallback', eventResult.error.message);
        return buildFallbackResult(config.defaultMeetUrl);
    }
    const event = eventResult.data;

    // ── Extract Meet join URL from conferenceData ────────────────────────
    const meetJoinUrl = extractMeetJoinUrl(event.conferenceData);
    if (!meetJoinUrl) {
        console.warn(LOG_TAG, 'No video entry point in response — using fallback');
        // Event was created but has no Meet link — still return the event ID
        // so we can link it later, but fall back for the join URL.
        const fallbackUrl = resolveGoogleMeetJoinUrl({ defaultMeetUrl: config.defaultMeetUrl });
        return {
            joinUrl: fallbackUrl,
            externalMeetingId: event.conferenceData?.conferenceId ?? null,
            calendarEventId: event.id,
            providerStatus: 'error',
            providerMetadata: {
                googleEventId: event.id,
                htmlLink: event.htmlLink ?? null,
                conferenceData: event.conferenceData ?? null,
                fallbackReason: 'no_video_entry_point',
            },
        };
    }

    // ── Success: provider-backed Meet link ───────────────────────────────
    return {
        joinUrl: meetJoinUrl,
        externalMeetingId: event.conferenceData?.conferenceId ?? null,
        calendarEventId: event.id,
        providerStatus: 'created',
        providerMetadata: {
            googleEventId: event.id,
            htmlLink: event.htmlLink ?? null,
            conferenceId: event.conferenceData?.conferenceId ?? null,
            conferenceSolution: event.conferenceData?.conferenceSolution ?? null,
            entryPoints: event.conferenceData?.entryPoints ?? null,
        },
    };
}

// ─────────────────────────────────────────────────────────────────────────────
// Update / Cancel operations
// ─────────────────────────────────────────────────────────────────────────────

/** Metadata returned after a successful provider event update. */
export interface UpdatedMeetingMetadata {
    /** The provider calendar event ID (unchanged unless re-created) */
    calendarEventId: string;
    /** Join URL — may change if provider regenerated the meeting link */
    joinUrl: string | null;
}

/** Fields that can be patched on an existing Google Calendar event. */
export interface GoogleEventPatch {
    startTime?: string;
    endTime?: string;
    summary?: string;
    description?: string;
    /** Email addresses to set as attendees */
    attendees?: string[];
}

/**
 * Updates an existing Google Calendar event.
 * Only sends the fields included in `patch`.
 * Returns updated meeting metadata on success, null on failure (logged, non-throwing).
 */
export async function updateGoogleCalendarEvent(
    calendarEventId: string,
    patch: GoogleEventPatch,
): Promise<UpdatedMeetingMetadata | null> {
    if (isDemoMode) {
        console.info(LOG_TAG, 'Demo mode — skipping Google Calendar event update');
        return { calendarEventId, joinUrl: null };
    }

    const authService = GoogleCalendarAuthService.getInstance();
    if (!authService.isConnected()) return null;

    const tokenResult = await authService.getValidAccessToken();
    if (!tokenResult.success) return null;

    const syncService = GoogleCalendarSyncService.getInstance();
    const calendarResult = await syncService.getAx1CalendarId();
    if (!calendarResult.success) return null;

    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const body: Record<string, unknown> = {};
    if (patch.startTime) body.start = { dateTime: patch.startTime, timeZone };
    if (patch.endTime) body.end = { dateTime: patch.endTime, timeZone };
    if (patch.summary !== undefined) body.summary = patch.summary;
    if (patch.description !== undefined) body.description = patch.description;
    if (patch.attendees) {
        body.attendees = patch.attendees.map((email) => ({ email }));
    }

    try {
        const url = `${GOOGLE_CALENDAR_API}/calendars/${encodeURIComponent(calendarResult.data)}/events/${encodeURIComponent(calendarEventId)}`;
        const response = await fetch(url, {
            method: 'PATCH',
            headers: {
                Authorization: `Bearer ${tokenResult.data}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
        });
        if (!response.ok) {
            console.warn(LOG_TAG, `Event update failed: HTTP ${response.status}`);
            return null;
        }

        const event: GoogleEventResponse = await response.json();
        const joinUrl = extractMeetJoinUrl(event.conferenceData);
        return { calendarEventId: event.id || calendarEventId, joinUrl };
    } catch (err) {
        console.warn(LOG_TAG, 'Event update error:', err);
        return null;
    }
}

/** Result returned by a provider cancellation operation. */
export interface MeetingCancellationResult {
    /** Whether the provider-side deletion succeeded */
    cancelled: boolean;
    /** The external event ID that was cancelled */
    calendarEventId: string;
    /** Metadata patch to apply to the local calendar event */
    metadataPatch: {
        meetingProvider: null;
        meetingJoinUrl: null;
        meetingExternalEventId: null;
        meetingSyncStatus: 'synced';
    };
}

/**
 * Cancels (deletes) a Google Calendar event by ID.
 * Returns a cancellation result with confirmation and metadata patch,
 * or null on failure (logged, non-throwing).
 * Does NOT delete the local calendar event.
 */
export async function cancelGoogleCalendarEvent(
    calendarEventId: string,
): Promise<MeetingCancellationResult | null> {
    const metadataPatch = {
        meetingProvider: null as null,
        meetingJoinUrl: null as null,
        meetingExternalEventId: null as null,
        meetingSyncStatus: 'synced' as const,
    };

    if (isDemoMode) {
        console.info(LOG_TAG, 'Demo mode — skipping Google Calendar event cancel');
        return { cancelled: true, calendarEventId, metadataPatch };
    }

    const authService = GoogleCalendarAuthService.getInstance();
    if (!authService.isConnected()) return null;

    const tokenResult = await authService.getValidAccessToken();
    if (!tokenResult.success) return null;

    const syncService = GoogleCalendarSyncService.getInstance();
    const calendarResult = await syncService.getAx1CalendarId();
    if (!calendarResult.success) return null;

    try {
        const url = `${GOOGLE_CALENDAR_API}/calendars/${encodeURIComponent(calendarResult.data)}/events/${encodeURIComponent(calendarEventId)}`;
        const response = await fetch(url, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${tokenResult.data}` },
        });
        // 204 No Content = success, 410 Gone = already deleted (also OK)
        if (!response.ok && response.status !== 410) {
            console.warn(LOG_TAG, `Event cancel failed: HTTP ${response.status}`);
            return null;
        }
        return { cancelled: true, calendarEventId, metadataPatch };
    } catch (err) {
        console.warn(LOG_TAG, 'Event cancel error:', err);
        return null;
    }
}
